import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertTriangle, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { apiJson } from '@/lib/api';
import type {
  AdminPerfEndpointsResponse,
  AdminPerfOverviewResponse,
  AdminPerfRange,
  AdminPerfSort,
} from '@/lib/types';

const RANGE_OPTIONS: { range: AdminPerfRange; label: string; description: string }[] = [
  { range: '24h', label: '24h', description: 'Last 24 hours' },
  { range: '7d', label: '7d', description: 'Last 7 days' },
];

const ALLOWED_RANGES = new Set<string>(RANGE_OPTIONS.map((r) => r.range));

const SORT_OPTIONS: { sort: AdminPerfSort; label: string }[] = [
  { sort: 'count', label: 'Requests' },
  { sort: 'p95', label: 'p95 latency' },
  { sort: 'error_rate', label: 'Error rate' },
];

type ChartPoint = { label: string; sublabel: string; value: number };

function formatMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })}s`;
  return `${ms.toLocaleString(undefined, { maximumFractionDigits: ms < 10 ? 1 : 0 })}ms`;
}

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

function formatInt(value: number): string {
  return value.toLocaleString();
}

function buildPoints(
  points: AdminPerfOverviewResponse['points'],
  range: AdminPerfRange,
  pick: (p: AdminPerfOverviewResponse['points'][number]) => number,
): ChartPoint[] {
  return points.map((p) => {
    const dt = new Date(p.timestamp);
    const valid = !Number.isNaN(dt.getTime());
    const label = valid
      ? range === '24h'
        ? dt.toLocaleTimeString(undefined, { hour: 'numeric' })
        : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : '';
    const sublabel = valid
      ? dt.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
        })
      : p.timestamp;
    return { label, sublabel, value: pick(p) };
  });
}

function niceAxisMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function xLabelIndices(count: number): Set<number> {
  if (count <= 1) return new Set([0]);
  if (count <= 12) return new Set(Array.from({ length: count }, (_, i) => i));
  const target = 8;
  const step = Math.max(1, Math.round((count - 1) / (target - 1)));
  const indices = new Set<number>([0]);
  for (let i = step; i < count - 1; i += step) indices.add(i);
  indices.add(count - 1);
  return indices;
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>{value}</p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function PerfBarChart({
  title,
  subtitle,
  yAxisLabel,
  barColor,
  points,
  formatValue,
}: {
  title: string;
  subtitle: string;
  yAxisLabel: string;
  barColor: string;
  points: ChartPoint[];
  formatValue: (value: number) => string;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const updateWidth = () => {
      const next = el.clientWidth;
      if (next > 0) setChartWidth(next);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!points.length || chartWidth <= 0) return null;
    const n = points.length;
    const barGap = n <= 24 ? 2 : 1;
    const padLeft = 56;
    const padRight = 12;
    const padTop = 16;
    const padBottom = 40;
    const width = chartWidth;
    const height = 260;
    const innerW = width - padLeft - padRight;
    const innerH = height - padTop - padBottom;
    const slotW = innerW / n;
    const barW = Math.max(2, slotW - barGap);
    const maxValue = Math.max(...points.map((p) => p.value), 0);
    const axisMax = niceAxisMax(maxValue);
    const labelIndices = xLabelIndices(n);
    return {
      n,
      barGap,
      barW,
      slotW,
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      innerW,
      innerH,
      axisMax,
      labelIndices,
    };
  }, [points, chartWidth]);

  if (!points.length) {
    return <p className='text-sm text-gray-500'>No data in this range.</p>;
  }

  const ticks = layout ? [0, 0.25, 0.5, 0.75, 1].map((f) => layout.axisMax * f) : [];
  const hoveredPoint = hoveredIndex !== null && layout ? points[hoveredIndex] : null;
  const hoveredBar =
    hoveredIndex !== null && layout
      ? (() => {
          const h =
            layout.axisMax > 0
              ? (points[hoveredIndex].value / layout.axisMax) * layout.innerH
              : 0;
          const x =
            layout.padLeft + hoveredIndex * layout.slotW + layout.barGap / 2;
          const y = layout.height - layout.padBottom - h;
          return { cx: x + layout.barW / 2, y };
        })()
      : null;
  const baselineY = layout ? layout.height - layout.padBottom : 0;

  return (
    <div className='min-w-0'>
      <div className='mb-3'>
        <p className='text-sm font-semibold text-gray-900'>{title}</p>
        <p className='text-xs text-gray-500'>{subtitle}</p>
      </div>
      <div
        ref={chartRef}
        className='rounded-md border border-gray-100 bg-gray-50/50 pb-1'
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {layout ? (
          <svg
            viewBox={`0 0 ${layout.width} ${layout.height}`}
            width='100%'
            height={layout.height}
            role='img'
            aria-label={`${title} bar chart`}
          >
            <text
              x={14}
              y={layout.padTop + layout.innerH / 2}
              transform={`rotate(-90 14 ${layout.padTop + layout.innerH / 2})`}
              textAnchor='middle'
              className='fill-gray-500 text-[11px] font-medium'
            >
              {yAxisLabel}
            </text>

            {ticks.map((tick) => {
              const y = baselineY - (tick / layout.axisMax) * layout.innerH;
              return (
                <g key={tick}>
                  <line
                    x1={layout.padLeft}
                    y1={y}
                    x2={layout.padLeft + layout.innerW}
                    y2={y}
                    stroke={tick === 0 ? '#d1d5db' : '#e5e7eb'}
                    strokeDasharray={tick === 0 ? undefined : '4 4'}
                  />
                  <text
                    x={layout.padLeft - 8}
                    y={y + 4}
                    textAnchor='end'
                    className='fill-gray-500 text-[10px] tabular-nums'
                  >
                    {formatValue(tick)}
                  </text>
                </g>
              );
            })}

            {points.map((p, i) => {
              const h = layout.axisMax > 0 ? (p.value / layout.axisMax) * layout.innerH : 0;
              const x = layout.padLeft + i * layout.slotW + layout.barGap / 2;
              const y = baselineY - h;
              const cx = x + layout.barW / 2;
              const isHovered = hoveredIndex === i;
              return (
                <g key={i}>
                  <rect
                    x={layout.padLeft + i * layout.slotW}
                    y={layout.padTop}
                    width={layout.slotW}
                    height={layout.innerH}
                    fill='transparent'
                    className='cursor-pointer'
                    onMouseEnter={() => setHoveredIndex(i)}
                  />
                  <rect
                    x={x}
                    y={y}
                    width={layout.barW}
                    height={Math.max(0, h)}
                    fill={barColor}
                    fillOpacity={isHovered ? 1 : hoveredIndex !== null ? 0.55 : 0.85}
                    rx={2}
                    className='pointer-events-none'
                  />
                  {layout.labelIndices.has(i) ? (
                    <text
                      x={cx}
                      y={baselineY + 14}
                      textAnchor='middle'
                      className='fill-gray-600 text-[10px] pointer-events-none'
                    >
                      {p.label}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {hoveredPoint && hoveredBar ? (
              <BarChartHoverLabel
                x={hoveredBar.cx}
                barTopY={hoveredBar.y}
                minX={layout.padLeft + 30}
                maxX={layout.padLeft + layout.innerW - 30}
                minY={layout.padTop + 4}
                text={`${hoveredPoint.sublabel} · ${formatValue(hoveredPoint.value)}`}
              />
            ) : null}
          </svg>
        ) : (
          <div className='h-[260px]' />
        )}
      </div>
    </div>
  );
}

function methodBadgeClass(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'bg-sky-100 text-sky-700';
    case 'POST':
      return 'bg-emerald-100 text-emerald-700';
    case 'PUT':
    case 'PATCH':
      return 'bg-amber-100 text-amber-700';
    case 'DELETE':
      return 'bg-rose-100 text-rose-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function AdminPerformancePage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { range: rangeParam } = Route.useSearch();
  const range: AdminPerfRange = ALLOWED_RANGES.has(rangeParam ?? '')
    ? (rangeParam as AdminPerfRange)
    : '24h';
  const [sort, setSort] = useState<AdminPerfSort>('count');

  const activeRangeOption =
    RANGE_OPTIONS.find((opt) => opt.range === range) ?? RANGE_OPTIONS[0];

  const overviewQuery = useQuery({
    queryKey: ['admin', 'performance', 'overview', range],
    queryFn: () =>
      apiJson<AdminPerfOverviewResponse>(
        getToken,
        `/v1/admin/performance/overview?range=${encodeURIComponent(range)}`,
      ),
  });

  const endpointsQuery = useQuery({
    queryKey: ['admin', 'performance', 'endpoints', range, sort],
    queryFn: () =>
      apiJson<AdminPerfEndpointsResponse>(
        getToken,
        `/v1/admin/performance/endpoints?range=${encodeURIComponent(range)}&sort=${encodeURIComponent(sort)}`,
      ),
  });

  const overview = overviewQuery.data;
  const endpoints = endpointsQuery.data;
  const isLoading = overviewQuery.isLoading || endpointsQuery.isLoading;
  const isFetching = overviewQuery.isFetching || endpointsQuery.isFetching;

  const throughputPoints = useMemo(
    () => (overview ? buildPoints(overview.points, range, (p) => p.request_count) : []),
    [overview, range],
  );
  const latencyPoints = useMemo(
    () => (overview ? buildPoints(overview.points, range, (p) => p.p95_ms) : []),
    [overview, range],
  );

  const setRange = (next: AdminPerfRange) => {
    navigate({ to: '/admin/performance', search: { range: next } });
  };

  const handleRefresh = () => {
    void overviewQuery.refetch();
    void endpointsQuery.refetch();
  };

  const notConfigured = overview && !overview.configured;

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold text-gray-900 sm:text-2xl'>Performance</h1>
          <p className='mt-1 text-sm text-gray-500'>
            Request throughput, latency, and error rates from the API timing middleware. Hourly
            buckets (UTC), backed by Redis.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <div className='inline-flex flex-col gap-1.5' role='group' aria-label='Time range'>
            <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-0.5'>
              Time range
            </span>
            <div className='inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm'>
              {RANGE_OPTIONS.map((opt) => {
                const active = range === opt.range;
                return (
                  <button
                    key={opt.range}
                    type='button'
                    aria-pressed={active}
                    title={opt.description}
                    onClick={() => setRange(opt.range)}
                    className={`min-w-[3rem] rounded-md px-3 py-2 text-xs font-bold transition-all ${
                      active
                        ? 'bg-primary text-white shadow-sm ring-2 ring-primary/30'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type='button'
            onClick={handleRefresh}
            disabled={isFetching}
            className='inline-flex items-center gap-1.5 self-end rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60'
          >
            <FiRefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : (
        <>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500'>
            <span className='inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary'>
              {activeRangeOption.label} · {activeRangeOption.description}
            </span>
            <span className='inline-flex items-center gap-1.5'>
              <FiInfo className='h-3.5 w-3.5 shrink-0' aria-hidden />
              Excludes static, health, and performance endpoints.
            </span>
          </div>

          {notConfigured ? (
            <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-2'>
              <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
              <div className='min-w-0 space-y-1'>
                <p className='font-semibold'>Performance metrics unavailable</p>
                <p className='text-xs'>
                  <span className='font-mono'>REDIS_URL</span> is not configured on the API
                  deployment, so request metrics are not being recorded.
                </p>
              </div>
            </div>
          ) : null}

          {overviewQuery.isError || !overview ? (
            <p className='text-red-600'>Could not load performance overview.</p>
          ) : (
            <>
              <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6'>
                <StatTile label='Total requests' value={formatInt(overview.total_requests)} />
                <StatTile
                  label='Avg req/min'
                  value={overview.avg_requests_per_minute.toLocaleString(undefined, {
                    maximumFractionDigits: 1,
                  })}
                />
                <StatTile label='Avg latency' value={formatMs(overview.avg_ms)} />
                <StatTile label='p95 latency' value={formatMs(overview.p95_ms)} />
                <StatTile label='p99 latency' value={formatMs(overview.p99_ms)} />
                <StatTile
                  label='5xx error rate'
                  value={formatPercent(overview.error_rate)}
                  hint={`4xx: ${formatPercent(overview.client_error_rate)}`}
                />
              </div>

              <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
                <PerfBarChart
                  title='Requests per hour'
                  subtitle='Total matched API requests per hour (UTC)'
                  yAxisLabel='Requests'
                  barColor='#018A9D'
                  points={throughputPoints}
                  formatValue={formatInt}
                />
              </section>

              <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
                <PerfBarChart
                  title='p95 latency per hour'
                  subtitle='95th percentile request latency per hour (UTC)'
                  yAxisLabel='Latency'
                  barColor='#6366f1'
                  points={latencyPoints}
                  formatValue={formatMs}
                />
              </section>
            </>
          )}

          <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
            <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
              <div>
                <p className='text-sm font-semibold text-gray-900'>Endpoints</p>
                <p className='text-xs text-gray-500'>
                  Aggregated over the selected range. Top 100 by the chosen sort.
                </p>
              </div>
              <div className='inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm'>
                {SORT_OPTIONS.map((opt) => {
                  const active = sort === opt.sort;
                  return (
                    <button
                      key={opt.sort}
                      type='button'
                      aria-pressed={active}
                      onClick={() => setSort(opt.sort)}
                      className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
                        active
                          ? 'bg-primary text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {endpointsQuery.isError ? (
              <p className='text-red-600'>Could not load endpoint metrics.</p>
            ) : !endpoints || endpoints.endpoints.length === 0 ? (
              <p className='text-sm text-gray-500'>No endpoint data in this range.</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead>
                    <tr className='border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500'>
                      <th className='py-2 pr-3 font-semibold'>Endpoint</th>
                      <th className='py-2 px-3 text-right font-semibold'>Requests</th>
                      <th className='py-2 px-3 text-right font-semibold'>Avg</th>
                      <th className='py-2 px-3 text-right font-semibold'>p95</th>
                      <th className='py-2 pl-3 text-right font-semibold'>5xx rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpoints.endpoints.map((row) => (
                      <tr
                        key={`${row.method} ${row.template}`}
                        className='border-b border-gray-100 last:border-0'
                      >
                        <td className='py-2 pr-3'>
                          <span className='flex items-center gap-2'>
                            <span
                              className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${methodBadgeClass(row.method)}`}
                            >
                              {row.method}
                            </span>
                            <span className='font-mono text-xs text-gray-800 break-all'>
                              {row.template}
                            </span>
                          </span>
                        </td>
                        <td className='py-2 px-3 text-right tabular-nums text-gray-900'>
                          {formatInt(row.count)}
                        </td>
                        <td className='py-2 px-3 text-right tabular-nums text-gray-700'>
                          {formatMs(row.avg_ms)}
                        </td>
                        <td className='py-2 px-3 text-right tabular-nums text-gray-700'>
                          {formatMs(row.p95_ms)}
                        </td>
                        <td
                          className={`py-2 pl-3 text-right tabular-nums ${
                            row.error_rate > 0 ? 'font-semibold text-rose-600' : 'text-gray-700'
                          }`}
                        >
                          {formatPercent(row.error_rate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/performance')({
  validateSearch: (search: Record<string, unknown>) => ({
    range:
      typeof search.range === 'string' && ALLOWED_RANGES.has(search.range)
        ? (search.range as AdminPerfRange)
        : ('24h' as AdminPerfRange),
  }),
  component: AdminPerformancePage,
});
